import { useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { m } from 'framer-motion';
import { Layout } from '../components/layout';
import {
  usePostBySlug,
  usePosts,
  useTags,
  useTagNameLookup,
  buildSizes,
  formatDate,
} from '@lightive/shared';
import { EASING } from '@lightive/ui';
import type { ContentType } from '@lightive/shared';
import { BlockRenderer } from '../components/BlockRenderer';

// Usage examples:
// <DetailPage contentType="work" backLink="/" relatedLabel="RELATED WORK" relatedAspectRatio="aspect-[4/3]" showClient showLocation notFoundRedirect="/" />
// <DetailPage contentType="lab" backLink="/lab" relatedLabel="RELATED EXPERIMENTS" relatedAspectRatio="aspect-square" notFoundRedirect="/lab" />

interface DetailPageConfig {
  contentType: ContentType;
  backLink: string;
  backLabel?: string;
  relatedLabel: string;
  relatedAspectRatio: string;
  showClient?: boolean;
  showLocation?: boolean;
  notFoundRedirect: string;
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const },
  },
};

export default function DetailPage({
  contentType,
  backLink,
  backLabel = 'BACK',
  relatedLabel,
  relatedAspectRatio,
  showClient = false,
  showLocation = false,
  notFoundRedirect,
}: DetailPageConfig) {
  const { slug } = useParams<{ slug: string }>();

  const { post, loading: postLoading } = usePostBySlug(slug, { status: 'published' });
  const { posts: allPosts, loading: postsLoading } = usePosts({
    type: contentType,
    status: 'published',
  });
  const { tags: allTags, loading: tagsLoading } = useTags();
  const { getTagNames } = useTagNameLookup(allTags);

  const relatedPosts = useMemo(() => {
    if (!post) return [];
    return allPosts
      .filter(
        (p) =>
          p.id !== post.id && p.tags.some((tag) => post.tags.includes(tag))
      )
      .slice(0, 3);
  }, [post, allPosts]);

  const { prevPost, nextPost } = useMemo(() => {
    if (!post) return { prevPost: null, nextPost: null };
    const currentIndex = allPosts.findIndex((p) => p.id === post.id);
    return {
      prevPost: currentIndex > 0 ? allPosts[currentIndex - 1] : null,
      nextPost: currentIndex < allPosts.length - 1 ? allPosts[currentIndex + 1] : null,
    };
  }, [post, allPosts]);

  if (postLoading || postsLoading || tagsLoading) {
    return (
      <Layout>
        <section className="px-6 md:px-10 py-8 md:py-12">
          <div className="animate-pulse">
            <div className="h-4 bg-white/5 w-24 mb-6" />
            <div className="h-10 bg-white/5 w-3/4 mb-4" />
            <div className="h-5 bg-white/5 w-48" />
          </div>
        </section>
      </Layout>
    );
  }

  if (!post || post.type !== contentType) {
    return <Navigate to={notFoundRedirect} replace />;
  }

  const relatedLinkPrefix = `/${contentType}`;

  return (
    <Layout>
      {/* Hero Section */}
      <section className="px-6 md:px-10 py-8 md:py-12">
        <m.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {/* Back Link */}
          <m.div variants={fadeInUp}>
            <Link
              to={backLink}
              className="inline-flex items-center gap-2 pb-0.5 font-en font-bold text-[11px] uppercase tracking-widest text-white/30 transition-[color,box-shadow] duration-200 hover:text-white hover:shadow-[inset_0_-1px_0_var(--color-rule)] mb-8"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {backLabel}
            </Link>
          </m.div>

          {/* Tags */}
          <m.div variants={fadeInUp} className="flex flex-wrap gap-3 mb-4">
            {getTagNames(post.tags).map((tagName) => (
              <span
                key={tagName}
                className="font-en font-bold text-[11px] uppercase tracking-widest text-[var(--color-ink-3)]"
              >
                {tagName}
              </span>
            ))}
          </m.div>

          {/* Title */}
          <m.h1
            variants={fadeInUp}
            className="font-en font-semibold text-3xl md:text-5xl uppercase tracking-tight text-white mb-8 leading-none min-w-0 [overflow-wrap:anywhere]"
          >
            {post.title.toUpperCase()}
          </m.h1>

          {/* Description */}
          {post.description && (
            <m.p
              variants={fadeInUp}
              className="font-kr text-lg md:text-xl text-white/70 leading-relaxed max-w-2xl mb-10"
            >
              {post.description}
            </m.p>
          )}

          {/* Meta table */}
          {((showClient && post.client) || post.date || (showLocation && post.location)) && (
            <m.div variants={fadeInUp}>
              <dl className="border-t border-[var(--color-rule)]">
                {showClient && post.client && (
                  <div className="flex items-center justify-between gap-4 py-3 border-b border-[var(--color-rule)]">
                    <dt className="font-en text-xs uppercase tracking-widest text-[var(--color-ink-3)]">
                      CLIENT
                    </dt>
                    <dd className="font-kr text-sm text-white text-right">{post.client}</dd>
                  </div>
                )}
                {post.date && (
                  <div className="flex items-center justify-between gap-4 py-3 border-b border-[var(--color-rule)]">
                    <dt className="font-en text-xs uppercase tracking-widest text-[var(--color-ink-3)]">
                      DATE
                    </dt>
                    <dd className="font-kr text-sm text-white text-right">{formatDate(post.date)}</dd>
                  </div>
                )}
                {showLocation && post.location && (
                  <div className="flex items-center justify-between gap-4 py-3 border-b border-[var(--color-rule)]">
                    <dt className="font-en text-xs uppercase tracking-widest text-[var(--color-ink-3)]">
                      LOCATION
                    </dt>
                    <dd className="font-kr text-sm text-white text-right">{post.location}</dd>
                  </div>
                )}
              </dl>
            </m.div>
          )}
        </m.div>
      </section>

      {/* Main Image */}
      <section className="pb-8">
        <m.div
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.3, ease: EASING.smooth }}
          className="overflow-hidden border-y-2 border-white/10"
        >
          <img
            src={post.thumbnail}
            srcSet={post.thumbnailSrcSet || undefined}
            sizes={post.thumbnailSrcSet ? buildSizes('full-width') : undefined}
            alt={post.title}
            className="w-full aspect-video object-cover"
            loading="eager"
            decoding="async"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        </m.div>
      </section>

      {/* WYSIWYG content blocks */}
      {post.content && post.content.length > 0 && (
        <section className="px-6 md:px-10 py-16 md:py-20 border-t-2 border-white/10">
          <div className="max-w-4xl mx-auto">
            <BlockRenderer blocks={post.content} />
          </div>
        </section>
      )}

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="px-6 md:px-10 py-12 md:py-16 border-t-2 border-white/10">
          <m.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="font-en font-bold text-[10px] uppercase tracking-widest text-white/25 mb-8">
              {relatedLabel}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`${relatedLinkPrefix}/${relatedPost.slug}`}
                  className="group block"
                >
                  <div className={`relative ${relatedAspectRatio} overflow-hidden bg-white/5 border-2 border-white/15`}>
                    <img
                      src={relatedPost.thumbnail}
                      srcSet={relatedPost.thumbnailSrcSet || undefined}
                      sizes={relatedPost.thumbnailSrcSet ? buildSizes('card') : undefined}
                      alt={relatedPost.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 ease-smooth group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="mt-4">
                    <h3 className="font-en font-bold text-sm uppercase tracking-tight text-white">
                      {relatedPost.title.toUpperCase()}
                    </h3>
                    {showClient && relatedPost.client && (
                      <p className="font-en font-bold text-[11px] uppercase tracking-wider text-white/30 mt-1">
                        {relatedPost.client.toUpperCase()}
                      </p>
                    )}
                    {!showClient && (
                      <span className="font-en font-bold text-[11px] uppercase tracking-wider text-white/30 mt-1 block">
                        {formatDate(relatedPost.date)}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </m.div>
        </section>
      )}

      {/* Previous / Next Navigation */}
      {(prevPost || nextPost) && (
        <section className="border-t-2 border-white/10">
          <div className="grid grid-cols-2">
            {prevPost ? (
              <Link
                to={`${relatedLinkPrefix}/${prevPost.slug}`}
                className="group px-6 md:px-10 py-8 md:py-12 border-r border-white/10 hover:bg-white/[0.02] transition-colors"
              >
                <span className="font-en font-bold text-[10px] uppercase tracking-widest text-white/25 block mb-2">
                  PREVIOUS
                </span>
                <span className="font-en font-bold text-sm md:text-base uppercase tracking-tight text-white/60 group-hover:text-white transition-colors">
                  {prevPost.title.toUpperCase()}
                </span>
              </Link>
            ) : (
              <div />
            )}
            {nextPost ? (
              <Link
                to={`${relatedLinkPrefix}/${nextPost.slug}`}
                className="group px-6 md:px-10 py-8 md:py-12 text-right hover:bg-white/[0.02] transition-colors"
              >
                <span className="font-en font-bold text-[10px] uppercase tracking-widest text-white/25 block mb-2">
                  NEXT
                </span>
                <span className="font-en font-bold text-sm md:text-base uppercase tracking-tight text-white/60 group-hover:text-white transition-colors">
                  {nextPost.title.toUpperCase()}
                </span>
              </Link>
            ) : (
              <div />
            )}
          </div>
        </section>
      )}
    </Layout>
  );
}
